import { ApiResponse, ImageCaptionDTO, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  CreatePierPayload,
  IPierData,
  PierListQueryParams,
  UpdatePierPayload,
} from "@/types/pier-management.types";

export const PierManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPiers: builder.query<
      PaginatedApiResponse<IPierData[]>,
      PierListQueryParams
    >({
      query: (params) => ({
        url: "berth-pier",
        method: "GET",
        params,
      }),
      transformResponse: (response: PaginatedApiResponse<IPierData[]>) =>
        response,
      providesTags: ["Piers"],
      keepUnusedDataFor: 0,
    }),
    getPierById: builder.query<IPierData, string>({
      query: (pier_id) => ({
        url: `berth-pier/${pier_id}`,
        method: "GET",
      }),
      transformResponse: (response: ApiResponse<IPierData>) => response.data,
      providesTags: (result, error, pier_id) => [
        { type: "Piers", id: pier_id },
      ],
      keepUnusedDataFor: 0,
    }),
    createPier: builder.mutation<ApiResponse<IPierData>, CreatePierPayload>({
      query: (data) => ({
        url: "berth-pier",
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<IPierData>) => response,
      invalidatesTags: ["Piers"],
    }),
    updatePier: builder.mutation<ApiResponse<void>, UpdatePierPayload>({
      query: ({
        name,
        harbor_id,
        pierId,
        boater_label,
        approach_note,
        boater_label_translations,
        approach_note_translations,
        capacity_mode,
        pooled_capacity,
      }) => ({
        url: `berth-pier/${pierId}`,
        method: "PATCH",
        data: {
          name,
          harbor_id,
          boater_label,
          approach_note,
          boater_label_translations,
          approach_note_translations,
          capacity_mode,
          pooled_capacity,
        },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Piers"],
    }),
    deletePier: builder.mutation<ApiResponse<void>, string>({
      query: (pier_id) => ({
        url: `berth-pier/${pier_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Piers"],
    }),
    uploadPierImage: builder.mutation<
      ApiResponse<IPierData>,
      { pier_id: string; file?: File; captions?: ImageCaptionDTO[] }
    >({
      query: ({ pier_id, file, captions }) => {
        const formData = new FormData();
        if (file) {
          formData.append("image", file);
        }
        if (captions?.length) {
          formData.append("captions", JSON.stringify(captions));
        }
        return {
          url: `berth-pier/${pier_id}/image`,
          method: "POST",
          data: formData,
        };
      },
      transformResponse: (response: ApiResponse<IPierData>) => response,
      invalidatesTags: (result, error, { pier_id }) => [
        { type: "Piers", id: pier_id },
        "Piers",
      ],
    }),
    removePierImage: builder.mutation<ApiResponse<IPierData>, string>({
      query: (pier_id) => ({
        url: `berth-pier/${pier_id}/image`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<IPierData>) => response,
      invalidatesTags: (result, error, pier_id) => [
        { type: "Piers", id: pier_id },
        "Piers",
      ],
    }),
  }),
});

export const {
  useGetPiersQuery,
  useGetPierByIdQuery,
  useCreatePierMutation,
  useUpdatePierMutation,
  useDeletePierMutation,
  useUploadPierImageMutation,
  useRemovePierImageMutation,
} = PierManagementService;
